import { useRef } from "react";

type UseInitialSummaryAnimationParams = {
    currentStep: number;
    isSummaryEnabled: boolean;
    isFetching: boolean;
};

/**
 * Animate the typing effect only for the step shown when the tooltip first opens, and only if
 * that summary had to be fetched (a cached summary appears instantly). Stepping between scores
 * via the pager never types — a switched-to step reveals its text immediately once loaded.
 *
 * The ref bookkeeping runs during render (not in an effect) so the first paint already knows
 * whether to type, avoiding a flash-then-animate.
 */
const useInitialSummaryAnimation = ({
    currentStep,
    isSummaryEnabled,
    isFetching,
}: UseInitialSummaryAnimationParams): boolean => {
    const initializedRef = useRef(false);
    const animateInitialStepRef = useRef(false);
    const prevStepRef = useRef(currentStep);
    const hasNavigatedRef = useRef(false);

    if (!initializedRef.current) {
        initializedRef.current = true;
        animateInitialStepRef.current = isSummaryEnabled && isFetching;
    }
    if (prevStepRef.current !== currentStep) {
        prevStepRef.current = currentStep;
        hasNavigatedRef.current = true;
    }

    return !hasNavigatedRef.current && animateInitialStepRef.current;
};

export default useInitialSummaryAnimation;
